import { problemLabels, orderSlugsByLabels } from "@/lib/intake-diag";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { isMethodAllowed, planForMethod } from "@/lib/booking-payment";

const createBookingInput = z.object({
  serviceId: z.string().uuid(),
  /** Legacy field — the wizard no longer picks a time. Accepted and ignored. */
  slotId: z.string().min(1).optional().nullable(),
  fullName: z.string().min(2),
  phone: z.string().min(6),
  country: z.string().min(2),
  city: z.string().optional(),
  preferredTimes: z.array(z.string()).default([]),
  intakeAnswers: z.record(z.string(), z.any()).optional(),
  paymentMethod: z.string().min(1),
  sessionMode: z.enum(["online", "in_person"]).default("online"),
  consentChildData: z.boolean().optional().default(false),
});

export const createBooking = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => createBookingInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const resolverInput = { country: data.country, sessionMode: data.sessionMode };
    const paymentMethod = data.paymentMethod;
    if (!isMethodAllowed(resolverInput, paymentMethod)) {
      throw new Error("Payment method is not available for this session type");
    }
    // The time is agreed on WhatsApp afterwards — cash is the only "pay later" plan.
    const plan = planForMethod(paymentMethod);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Upsert profile with contact info
    const { data: profileRow, error: profileErr } = await supabase.from("profiles").upsert(
      {
        user_id: userId,
        full_name: data.fullName,
        phone_whatsapp: data.phone,
        country: data.country,
        city: data.city ?? null,
      },
      { onConflict: "user_id" },
    ).select("id").single();
    if (profileErr) throw profileErr;
    const profileId = profileRow.id;

    // Save intake answers if provided
    let intakeId: string | null = null;
    if (data.intakeAnswers && Object.keys(data.intakeAnswers).length > 0) {
      const { data: form } = await supabase
        .from("forms")
        .select("id, schema")
        .eq("slug", "consultation-intake")
        .maybeSingle();
      if (form) {
        // Phase 3 — resolve the parents-language `main_problem` label into a
        // stable `main_problem_slug` from problem_catalog so downstream code
        // (case_profile lift, admin analytics, AI in Phase 5) doesn't depend
        // on Arabic label matching.
        // main_problem is one label (old) or up to 3 labels (multi-choice).
        const mainLabels = problemLabels((data.intakeAnswers as any)?.main_problem);
        let mainProblemSlugs: string[] = [];
        if (mainLabels.length > 0) {
          const { data: pcs } = await supabaseAdmin
            .from("problem_catalog")
            .select("slug, label_ar")
            .in("label_ar", mainLabels)
            .eq("is_active", true);
          mainProblemSlugs = orderSlugsByLabels(mainLabels, (pcs ?? []) as any[]);
        }
        const answersWithSlug = mainProblemSlugs.length
          ? { ...data.intakeAnswers, main_problem_slug: mainProblemSlugs[0], main_problem_slugs: mainProblemSlugs }
          : data.intakeAnswers;

        const { data: sub, error: subErr } = await supabase
          .from("form_submissions")
          .insert({
            form_id: form.id,
            client_id: profileId,
            answers: answersWithSlug,
            form_snapshot: form.schema,
            linked_entity: "booking",
          })
          .select("id")
          .single();
        if (subErr) throw subErr;
        intakeId = sub.id;

      }
    }

    // No time is reserved here — the team agrees it on WhatsApp afterwards.
    const { data: booking, error } = await supabase
      .from("bookings")
      .insert({
        client_id: profileId,
        service_id: data.serviceId,
        status: "new",
        preferred_times: data.preferredTimes,
        payment_method: paymentMethod,
        payment_status: "unpaid",
        session_mode: data.sessionMode,
        payment_plan: plan,
        intake_submission_id: intakeId,
        slot_id: null,
        scheduled_at: null,
        consent_child_data: data.consentChildData ?? false,
      })
      .select("id, status")
      .single();
    if (error) throw error;


    // Link submission back
    if (intakeId) {
      await supabase
        .from("form_submissions")
        .update({ linked_entity_id: booking.id })
        .eq("id", intakeId);
    }

    return { id: booking.id };
  });

export const listMyBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: profile } = await context.supabase
      .from("profiles").select("id").eq("user_id", context.userId).maybeSingle();
    if (!profile) return [];
    const { data, error } = await context.supabase
      .from("bookings")
      .select("id, status, payment_status, scheduled_at, created_at, service_id, receipt_file, meeting_link, receipt_rejection_reason, session_mode, payment_plan, payment_method, intake_submission_id")
      .eq("client_id", profile.id)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
  });

// --------- Receipt upload flow (called from /book/confirm/$id) ---------

export const getMyBooking = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    const { data: profile } = await context.supabase
      .from("profiles").select("id").eq("user_id", context.userId).maybeSingle();
    const { data: b, error } = await context.supabase
      .from("bookings")
      .select("id, status, payment_status, scheduled_at, meeting_link, service_id, payment_method, session_mode, payment_plan, client_id, receipt_file, receipt_rejection_reason, created_at")
      .eq("id", data.id)
      .eq("client_id", profile?.id ?? "")
      .maybeSingle();
    if (error) throw error;
    if (!b) throw new Error("Booking not found");
    const { data: svc } = await context.supabase
      .from("services")
      .select("slug, title_ar, description_ar, duration_min, price_usd")
      .eq("id", b.service_id)
      .maybeSingle();
    // bookings.client_id is a FK to profiles.id — look the client up by id, not user_id.
    const { data: client } = await context.supabase
      .from("profiles")
      .select("full_name")
      .eq("id", b.client_id)
      .maybeSingle();
    return { booking: b, service: svc ?? null, clientName: client?.full_name ?? "" };
  });

export const submitBookingReceipt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string; receiptPath: string; paymentMethod?: "shamcash" | "paypal" }) =>
    z
      .object({
        id: z.string().uuid(),
        receiptPath: z.string().min(1),
        paymentMethod: z.enum(["shamcash", "paypal"]).optional(),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    // Verify booking is owned by this user
    const { data: profile } = await context.supabase
      .from("profiles").select("id").eq("user_id", context.userId).maybeSingle();
    const { data: b } = await context.supabase
      .from("bookings")
      .select("id, client_id, payment_method")
      .eq("id", data.id)
      .maybeSingle();
    if (!b || !profile || b.client_id !== profile.id) throw new Error("Forbidden");
    // The receipt must live in the caller's own folder and name this booking.
    const prefix = `${context.userId}/${data.id}-`;
    if (
      !data.receiptPath.startsWith(prefix) ||
      data.receiptPath.includes("..") ||
      data.receiptPath.slice(prefix.length).includes("/")
    ) {
      throw new Error("Invalid receipt");
    }

    // Record the rail the client actually paid with. Keep a specific inside-Syria
    // method (e.g. haram) when the region didn't change.
    const insideMethods = ["shamcash", "haram"];
    let payment_method = b.payment_method;
    if (data.paymentMethod === "paypal") payment_method = "paypal";
    else if (data.paymentMethod === "shamcash" && !insideMethods.includes(b.payment_method ?? ""))
      payment_method = "shamcash";

    const { error } = await context.supabase
      .from("bookings")
      .update({
        receipt_file: data.receiptPath,
        payment_status: "receipt_uploaded",
        receipt_rejection_reason: null,
        payment_method,
      })
      .eq("id", data.id);
    if (error) throw error;

    return { ok: true };
  });